import { ResetPasswordState, RootState } from '@/store/types';
import { ActionTree, MutationTree, Module, GetterTree } from 'vuex';
import { clearErrors, resetPasswordValidationSchema, validateSchema } from '@/utils/validation';
import axios from '@/utils/axios';
import * as NProgress from 'nprogress';

export const state: ResetPasswordState = {
    password: '',
    passwordConfirmation: '',
    passwordStrength: {
        score: 0,
        meetsMinimumLengthRequirement: false,
        containsUpperCaseLetter: false,
        containsLowerCaseLetter: false,
        containsAtLeastOneNumber: false,
        containsSpecialCharacter: false,
        isUncommonPassword: false,
    },
    isSending: false,
    submitted: false,
    errors: {},
};

export const getters: GetterTree<ResetPasswordState, RootState> = {
    getFieldErrors: state => (field: string) => state.errors[field],
};

export const mutations: MutationTree<ResetPasswordState> = {
    setPassword(state, payload) {
        state.password = payload.password;
        state.errors = clearErrors(state.errors, 'password');
    },
    setPasswordConfirmation(state, payload) {
        state.passwordConfirmation = payload.passwordConfirmation;
        state.errors = clearErrors(state.errors, 'passwordConfirmation');
    },
    setPasswordStrength(state, payload) {
        state.passwordStrength = payload.passwordStrength;
    },
    setIsSending(state, payload) {
        state.isSending = payload.isSending;
        if (state.isSending) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    },
    setSubmitted(state, payload) {
        state.submitted = payload.submitted;
    },
    setErrors(state, payload) {
        state.errors = payload.errors;
    },
    resetForm(state) {
        state.password = '';
        state.passwordConfirmation = '';
        state.isSending = false;
        state.submitted = false;
        state.errors = {};
    },
};

export const actions: ActionTree<ResetPasswordState, RootState> = {
    async resetPassword({ commit, state, rootState }, payload) {
        const errors = await validateSchema(resetPasswordValidationSchema, {
            password: state.password,
            passwordConfirmation: state.passwordConfirmation,
        });
        if (errors) {
            return commit('setErrors', { errors });
        }
        commit('setIsSending', { isSending: true });
        try {
            const response = await axios.post(`/${rootState.instanceId}/portal_reset`, {
                password: state.password,
                passwordResetToken: payload.passwordResetToken,
            });
            if (response.data?.success === false) {
                const message = response.data?.message;
                commit('setErrors', { errors: { global: [message] } });
                return;
            }
            commit('setSubmitted', { submitted: true });
        } catch (err) {
            const message = err.response?.data?.message;
            commit('setErrors', { errors: { global: [message] } });
        } finally {
            commit('setIsSending', { isSending: false });
        }
    },
};

const ResetPasswordStore: Module<ResetPasswordState, RootState> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default ResetPasswordStore;
